/* eslint-disable @typescript-eslint/no-explicit-any */
// react-i18next v17 requires TypeScript 5+; this project is on TS 4.9.5.
// See src/i18n/index.ts for the project-wide workaround.
import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import InputField from '../../components/InputField/InputField';
import Button from '../../components/Button/Button';
import Checkbox from '../../components/Checkbox/Checkbox';
import styles from './loginForm.module.css';
import { useAuth } from '../../hooks/useAuth';
import { useAuthContext } from '../../contexts/AuthContext';
import { ProfileType } from '../../types/enums';
import { loginSchema, type LoginForm as LoginFormValues } from '../../schemas/auth.schema';
import LoginChooser from '../../components/LoginChooser/LoginChooser';
import { AvailableProfileDto } from '../../services/api';
import { normalizeApiError } from '../../types/api-errors';
import { useLanguage } from '../../contexts/LanguageContext';
import { isStaffAccessRemoved, STAFF_ACCESS_REMOVED_KEY } from '../../lib/auth/staffAccess';

const useTranslation = useTranslationUntyped as any;

interface LoginFormProps {
    // AB#1453: 'signup' added so LoginForm can navigate to the signup flow.
    toggleForm: (form: 'login' | 'forgetPassword' | 'partner' | 'signup') => void;
}

const ROLE_OPTIONS: { value: ProfileType; label: string }[] = [
    { value: ProfileType.SalonOwner, label: 'Salon Owner' },
    { value: ProfileType.SalonStaff, label: 'Salon Staff' },
    { value: ProfileType.SuperAdmin, label: 'Super Admin' }
];

/**
 * Login form.
 *
 * AB#1434 (Auth v2): when `REACT_APP_AUTH_V2_ENABLED=true` the persona picker
 * is hidden and the form posts to POST /api/v2/auth/login. The server resolves
 * the user's profile and returns either an active profile (→ navigate home)
 * or a chooser-pending state for multi-profile users (→ chooser screen, not
 * yet wired in this PBI — follow-up AB#1434.b).
 *
 * When the flag is off the legacy v1 flow runs unchanged — the backend's
 * AB#1432 change already relaxed the `userType` mismatch so this form works
 * even without its own changes, but with the picker the UX is unchanged.
 */
const LoginForm: React.FC<LoginFormProps> = ({ toggleForm }) => {
    const { t } = useTranslation('auth');
    const { t: tl } = useLanguage();
    const { login, loginV2, loading, isAuthV2Enabled } = useAuth();
    const { clearTokens } = useAuthContext();
    const [rememberMeChecked, setRememberMeChecked] = useState(false);
    const [loginError, setLoginError] = useState<string | undefined>(undefined);
    const [selectedRole, setSelectedRole] = useState<ProfileType>(ProfileType.SalonOwner);
    // AB#1434.b — post-login profile chooser for multi-profile users without
    // a LastActiveProfileId. Populated by loginV2 when activeProfileId === null.
    const [chooserProfiles, setChooserProfiles] = useState<AvailableProfileDto[] | null>(null);

    const { control, handleSubmit, formState: { errors } } = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: { email: '', password: '' },
    });

    const onSubmit = async (values: LoginFormValues) => {
        setLoginError(undefined);
        try {
            if (isAuthV2Enabled) {
                // AB#1434 v2 flow: identifier + password only.
                const result = await loginV2({
                    identifier: values.email,
                    password: values.password,
                });

                if (result && result.activeProfileId == null && result.availableProfiles.length > 1) {
                    // AB#1434.b — surface the chooser modal. loginV2 has
                    // already stored the identity-only token; the chooser
                    // will call switchProfileV2 to upgrade it to a
                    // profile-scoped token.
                    setChooserProfiles(result.availableProfiles as AvailableProfileDto[]);
                }
                return;
            }

            // Legacy v1 flow.
            await login({
                identifier: values.email,
                password: values.password,
                userType: selectedRole,
            });
        } catch (err: unknown) {
            // AB#1919 (WS-5b) — read the canonical AppError via the single
            // normalizer. `status` now prefers the RFC 7807 `status` member
            // (falls back to legacy `statusCode`), and `code` is resolved via
            // `errorCode ?? code`.
            const appError = normalizeApiError(err);
            const status = appError.status;
            // AB#1582 — auth-v2 rejects email logins with 400 + errorCode
            // `invalid_channel` (Majdi's PR #481). Without this branch the
            // FE catch fell to "network" which is misleading: the request
            // reached the BE just fine. Show the actionable phone-only hint.
            // Fall back to the nested validation-array shape (`errors.code[0]`)
            // for this legacy auth-v2 path, which predates the canonical body.
            const errorCode =
                appError.code !== 'UNKNOWN'
                    ? appError.code
                    : appError.errors?.code?.[0];
            if (isStaffAccessRemoved(err)) {
                // S1 (AB#2204, SG-11) — the staff record is inactive / removed.
                setLoginError(tl(STAFF_ACCESS_REMOVED_KEY));
            } else if (status === 400 && errorCode === 'invalid_channel') {
                setLoginError(t('login.errors.usePhone'));
            } else if (status === 401 || status === 404) {
                setLoginError(t('login.errors.invalidCredentials'));
            } else if (status === 423) {
                setLoginError(t('login.errors.locked'));
            } else if (status === 429) {
                setLoginError(t('login.errors.rateLimited'));
            } else {
                setLoginError(t('login.errors.network'));
            }
        }
    };

    const handleChooserCancel = () => {
        // User clicked "sign out and try again" — drop the identity-only
        // token so the next login attempt is clean.
        clearTokens();
        setChooserProfiles(null);
    };

    return (
      <>
        <form
            className={styles.loginForm}
            onSubmit={handleSubmit(onSubmit)}
            aria-label={t('login.ariaLabel')}
            noValidate
            // Phase 3E rev 16 — founder spec: never let the browser
            // autofill saved usernames/passwords into the auth shell.
            // `autoComplete="off"` on the form + `new-password` on the
            // password input is the most reliable cross-browser combo.
            autoComplete="off"
        >
            <h1 className={styles.loginText}>
                {isAuthV2Enabled ? t('login.title') : t('legacy.loginHeading')}
            </h1>

            {loginError && (
                <div className={styles.loginError} role="alert">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <circle cx="8" cy="8" r="7.5" stroke="currentColor" />
                        <path d="M8 4v5M8 11v1" stroke="currentColor" strokeLinecap="round" />
                    </svg>
                    {loginError}
                </div>
            )}

            {/*
              * Persona picker (role tabs). Hidden when Auth v2 is enabled —
              * the server resolves the role from the identifier after auth.
              */}
            {!isAuthV2Enabled && (
                <fieldset className={styles.roleSelector}>
                    <legend className={styles.roleLabel}>{t('legacy.rolePickerLegend')}</legend>
                    <div className={styles.roleTabs} role="radiogroup" aria-label="Account type">
                        {ROLE_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                role="radio"
                                aria-checked={selectedRole === opt.value}
                                className={`${styles.roleTab} ${selectedRole === opt.value ? styles.active : ''}`}
                                onClick={() => setSelectedRole(opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </fieldset>
            )}

            <div className={styles.loginFields}>
                <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                        <InputField
                            label={t('login.identifierLabel')}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t('login.identifierPlaceholder')}
                            // AB#1581 — was type="email" which made the
                            // browser silently block submit when a phone
                            // number was entered (HTML5 validity check).
                            // Field is "Email or phone" so use "text".
                            type="text"
                            name="email"
                            autoComplete="off"
                            inputMode="email"
                            required
                            feedback={errors.email ? 'error' : undefined}
                            feedbackMessage={errors.email?.message}
                        />
                    )}
                />

                <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                        <InputField
                            label={t('login.passwordLabel')}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={t('login.passwordPlaceholder')}
                            type="password"
                            name="password"
                            autoComplete="new-password"
                            required
                            feedback={errors.password ? 'error' : undefined}
                            feedbackMessage={errors.password?.message}
                        />
                    )}
                />
            </div>

            <div className={styles.buttonWrpr}>
                <Button
                    type="submit"
                    label={loading ? t('login.submitting') : t('login.submit')}
                    variant={loading ? 'disabled' : 'primary'}
                    disabled={loading}
                />
            </div>

            <div className={styles.loginOptions}>
                <div>
                    <Checkbox
                        label={t('login.rememberMe')}
                        onChange={setRememberMeChecked}
                        checked={rememberMeChecked}
                        id="remember-me"
                    />
                </div>
                <span className={styles.link}>
                    <Button
                        label={t('login.forgotPassword')}
                        onClick={() => toggleForm('forgetPassword')}
                        noAppearance={true}
                        size="small"
                    />
                </span>
            </div>

            {/* AB#1454 / AB#1453: Create Account CTA — only visible when Auth v2 is enabled.
                Redirects to the OTP self-registration flow at /form/signup. */}
            {isAuthV2Enabled && (
                <div className={styles.loginOptions} style={{ justifyContent: 'center' }}>
                    <span style={{ fontSize: 'var(--font-size-sm)' }}>
                        {t('login.newToDevanza')}{' '}
                    </span>
                    <span className={styles.link}>
                        <Button
                            label={t('login.register')}
                            onClick={() => toggleForm('signup')}
                            noAppearance={true}
                            size="small"
                        />
                    </span>
                </div>
            )}
        </form>
        {chooserProfiles && (
            <LoginChooser
                profiles={chooserProfiles}
                onCancel={handleChooserCancel}
            />
        )}
      </>
    );
};

export default LoginForm;
